import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { lookupBarcode } from '../utils/nutritionApi';

function BarcodeScanner({ onFoodFound, onClose }) {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState('');
  const [manualBarcode, setManualBarcode] = useState('');
  const [isDesktop, setIsDesktop] = useState(false);
  const scannerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    setIsDesktop(!isMobile);
  }, []);

  const handleBarcodeLookup = useCallback(async (barcode) => {
    setScannedBarcode(barcode);
    setLoading(true);
    setNotFound(false);
    setError(null);

    try {
      const food = await lookupBarcode(barcode);
      if (mountedRef.current) {
        onFoodFound(food);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setError(err.response?.data?.error || 'Failed to look up barcode');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [onFoodFound]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch (e) {
        // Scanner may already be stopped
      }
      scannerRef.current = null;
    }
    if (mountedRef.current) {
      setScanning(false);
    }
  }, []);

  const startScanner = useCallback(async () => {
    // Stop any existing scanner first
    await stopScanner();

    setError(null);
    setPermissionDenied(false);
    setNotFound(false);

    try {
      const html5QrCode = new Html5Qrcode('barcode-reader');
      scannerRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 280, height: 150 },
          aspectRatio: 1.0,
          formatsToSupport: [0, 1, 2, 3, 4, 5] // EAN_13, EAN_8, UPC_A, UPC_E, CODE_128, CODE_39
        },
        async (decodedText) => {
          await stopScanner();
          handleBarcodeLookup(decodedText);
        },
        () => {} // Ignore scan failures (continuous scanning)
      );

      if (mountedRef.current) {
        setScanning(true);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      const msg = err?.toString() || '';
      if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
        setPermissionDenied(true);
      } else {
        setError('Could not start camera. Please check permissions.');
      }
    }
  }, [handleBarcodeLookup, stopScanner]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, []);

  // Auto-start camera on mobile
  useEffect(() => {
    if (!isDesktop) {
      startScanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const barcode = manualBarcode.trim();
    if (barcode && /^\d{8,14}$/.test(barcode)) {
      handleBarcodeLookup(barcode);
    } else {
      setError('Enter a valid barcode (8-14 digits)');
    }
  };

  const handleRetry = async () => {
    setNotFound(false);
    setError(null);
    setScannedBarcode('');
    if (!isDesktop) {
      await startScanner();
    }
  };

  const handleClose = async () => {
    await stopScanner();
    onClose();
  };

  return (
    <div className="barcode-overlay">
      <div className="barcode-modal">
        <div className="barcode-header">
          <h3>Scan Barcode</h3>
          <button className="barcode-close" onClick={handleClose}>&times;</button>
        </div>

        {/* Camera viewfinder */}
        {!loading && !notFound && !permissionDenied && (
          <div className="barcode-viewfinder">
            <div id="barcode-reader" className="barcode-reader"></div>
            {scanning && (
              <div className="barcode-scan-hint">
                Point camera at a barcode on the package
              </div>
            )}
          </div>
        )}

        {/* Permission denied */}
        {permissionDenied && (
          <div className="barcode-message">
            <div className="barcode-message-icon">&#128247;</div>
            <p>Camera access is needed to scan barcodes.</p>
            <p className="text-muted">You can enable it in your browser settings.</p>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="barcode-message">
            <div className="loading-spinner"></div>
            <p>Looking up barcode {scannedBarcode}...</p>
          </div>
        )}

        {/* Not found */}
        {notFound && (
          <div className="barcode-message">
            <div className="barcode-message-icon">&#128533;</div>
            <p>Product not found for barcode {scannedBarcode}</p>
            <p className="text-muted">Try searching manually instead.</p>
            <div className="barcode-actions">
              <button className="btn btn-outline" onClick={handleRetry}>
                Scan Again
              </button>
              <button className="btn btn-primary" onClick={handleClose}>
                Search Manually
              </button>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && !permissionDenied && (
          <div className="barcode-message">
            <div className="barcode-message-icon">&#9888;</div>
            <p>{error}</p>
            <button className="btn btn-outline" onClick={handleRetry}>
              Try Again
            </button>
          </div>
        )}

        {/* Manual barcode input (always shown as fallback) */}
        <div className="barcode-manual">
          <p className="barcode-manual-label">
            {isDesktop ? 'Enter barcode number:' : 'Or type the barcode number:'}
          </p>
          <form className="barcode-manual-form" onSubmit={handleManualSubmit}>
            <input
              type="text"
              className="barcode-manual-input"
              value={manualBarcode}
              onChange={(e) => setManualBarcode(e.target.value.replace(/\D/g, ''))}
              placeholder="e.g. 5901234123457"
              inputMode="numeric"
              pattern="\d*"
              maxLength={14}
              autoFocus={isDesktop}
            />
            <button
              type="submit"
              className="btn btn-primary barcode-manual-btn"
              disabled={loading || !manualBarcode.trim()}
            >
              Look Up
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BarcodeScanner;
